import {Component, inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogActions, MatDialogClose, MatDialogContent} from '@angular/material/dialog';
import {MatButton} from '@angular/material/button';
import {TranslatePipe} from '@ngx-translate/core';
import {AsyncPipe} from '@angular/common';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject, combineLatest, map, Observable, startWith} from 'rxjs';
import {MatAutocompleteModule, MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';
import {MatFormField, MatLabel, MatPrefix} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {MatIcon} from '@angular/material/icon';
import {Team, TeamHal} from '../../models/HAL/team-hal.model';
import {environment} from '../../../environments/environment';
import {SnackBarService} from '../../services/snackbar.service';

@Component({
  selector: 'app-move-case',
  imports: [
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatButton,
    TranslatePipe,
    AsyncPipe,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatPrefix,
    MatIcon
  ],
  templateUrl: './move-case.component.html',
  styleUrl: './move-case.component.scss'
})
export class MoveCaseComponent implements OnInit {
  protected data = inject(MAT_DIALOG_DATA);
  protected chosenTeam: Team | null = null;
  protected teamSearchControl: FormControl = new FormControl('');
  protected filteredTeams: Observable<Team[]> = new Observable<Team[]>();

  protected readonly caseName: string = '';

  private readonly currentTeamId: string = '';
  private readonly teams$ = new BehaviorSubject<Team[]>([]);

  constructor(private readonly httpClient: HttpClient, private readonly snackBarService: SnackBarService) {
    this.currentTeamId = this.data?.currentTeamId ?? '';
    this.caseName = this.data?.caseName ?? '';
  }

  ngOnInit(): void {
    this.httpClient.get(`${environment.backendUrl}/teams?scope=all`).subscribe({
      next: (data: any) => {
        const teamHal = new TeamHal(data);
        this.teams$.next(this.excludeCurrentTeamAndSort(teamHal._embedded?.teams ?? []));
      },
      // Without this the dropdown just stays empty and reads like "this tenant has no other team".
      error: () => this.snackBarService.open('moveCaseDialog.snackbars.loadTeamsError', 3, 'error')
    });

    const searchValueChanges = this.teamSearchControl.valueChanges.pipe(startWith(''));
    searchValueChanges.subscribe(value => {
      if (typeof value === 'string') {
        this.chosenTeam = null;
      }
    });
    this.filteredTeams = combineLatest([this.teams$, searchValueChanges]).pipe(
      map(([teams, value]) => this.filterTeams(teams, typeof value === 'string' ? value : (value as Team)?.name ?? ''))
    );
  }

  protected displayTeamName(team: Team | null): string {
    return team?.name ?? '';
  }

  protected teamSelected(event: MatAutocompleteSelectedEvent) {
    this.chosenTeam = event.option.value;
  }

  private filterTeams(teams: Team[], searchValue: string): Team[] {
    const normalizedSearchValue = searchValue.toLowerCase();
    return teams.filter(team => team.name.toLowerCase().includes(normalizedSearchValue));
  }

  private excludeCurrentTeamAndSort(teams: Team[]): Team[] {
    return teams
      .filter(team => team.identification !== this.currentTeamId)
      .sort((first, second) => first.name.localeCompare(second.name));
  }
}
